import ENV from "@/utils/env";
import { api } from "@/lib/api";
import { StudyType } from "../studies/types/study.type";
import { GetAllAssetInsightsReq, GetAllSceneInsightsReq } from "@/api/studio/insights/api";

/* Request Types */
export interface CreatePowerPointReq {
  projectId: string;
  studyId: string;
  orderBy?: GetAllAssetInsightsReq["orderBy"] | GetAllSceneInsightsReq["orderBy"];
  sortBy?: GetAllAssetInsightsReq["sortBy"];
}

/* Response Types */
export interface CreatePowerPointResp {
  taskId: string;
}

export interface GetPowerPointStatusResp {
  status: "RUNNING" | "SUCCEEDED" | "FAILED";
}

export interface GetPowerPointResp {
  fileUrl: string;
}

export interface CreateImagesResp {
  taskId: string;
}

export interface GetImagesResp {
  fileUrl: string;
}

export interface DownloadDataResp {
  fileUrl: string;
  filename: string;
}

/*
 * createPowerPoint endpoint
 *
 * This endpoint is used to create a PowerPoint presentation for a study.
 */
export const createPowerPoint = async ({ projectId, studyId, orderBy, sortBy }: CreatePowerPointReq) => {
  const resp = await api.post(`${ENV.API_PLATFORM_URL}/v3/studio/projects/${projectId}/studies/${studyId}/exports/ppt`, undefined, { params: { orderBy, sortBy } });
  return resp.data as CreatePowerPointResp;
};

/*
 * getPowerPointStatus endpoint
 *
 * This endpoint is used to get the status of a PowerPoint presentation create.
 */
export const getPowerPointStatus = async (projectId: string, studyId: string, taskId: string) => {
  const resp = await api.post(`${ENV.API_PLATFORM_URL}/v3/studio/projects/${projectId}/studies/${studyId}/exports/status`, { taskId });
  return resp.data as GetPowerPointStatusResp;
};

/*
 * getPowerPoint endpoint
 *
 * This endpoint returns a pre-signed URL to download the PowerPoint presentation.
 */
export const getPowerPoint = async (projectId: string, studyId: string) => {
  const resp = await api.get(`${ENV.API_PLATFORM_URL}/v3/studio/projects/${projectId}/studies/${studyId}/exports/ppt`);
  return resp.data as GetPowerPointResp;
};

/*
 * createImages endpoint
 *
 * Initiates an async visuals export for a study. Returns a taskId to poll for status.
 */
export const createImages = async (projectId: string, studyId: string, type: StudyType) => {
  const resp = await api.post(`${ENV.API_PLATFORM_URL}/v3/studio/projects/${projectId}/studies/${studyId}/exports/images`, { type });
  return resp.data as CreateImagesResp;
};

/*
 * getImages endpoint
 *
 * Returns a pre-signed URL to download the visuals export ZIP once generation has succeeded.
 */
export const getImages = async (projectId: string, studyId: string) => {
  const resp = await api.get(`${ENV.API_PLATFORM_URL}/v3/studio/projects/${projectId}/studies/${studyId}/exports/images`);
  return resp.data as GetImagesResp;
};

/*
 * downloadData endpoint
 *
 * Fetches the study data export (CSV) directly as a blob and wraps it in an object URL.
 * Unlike PowerPoint/visuals, this endpoint returns the file inline so there is nothing to poll.
 */
export const downloadData = async (projectId: string, studyId: string): Promise<DownloadDataResp> => {
  const resp = await api.get(`${ENV.API_PLATFORM_URL}/v3/studio/projects/${projectId}/studies/${studyId}/exports/csv`, { responseType: "blob" });

  return {
    fileUrl: URL.createObjectURL(resp.data as Blob),
    filename: parseFilename(resp.headers["content-disposition"]),
  };
};

/*
 * Pulls the filename out of a Content-Disposition header, falling back to a sensible default.
 */
const parseFilename = (contentDisposition?: string): string => {
  const fallback = "data-export.csv";
  if (!contentDisposition) return fallback;

  // Handles both `filename="name.csv"` and RFC 5987 `filename*=UTF-8''name.csv`.
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(contentDisposition);
  return match ? decodeURIComponent(match[1]) : fallback;
};
